import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import {
  createBreadcrumbWithI18n,
  usePathParams,
} from '~/apps/ninesegsAnalyzer/routes/paths'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useLogEventOnMount } from '~/hooks/useLogEventOnMount'
import { ensureExists } from '~/utils'

import { DocumentQaFilters } from './components/DocumentQaFilters'
import { DocumentQaForm } from './components/DocumentQaForm'
import { DocumentQaResults } from './components/DocumentQaResults'
import { useAskDocumentQuestion } from './hooks/useAskDocumentQuestion'
import { useDocumentQaFilters } from './hooks/useDocumentQaFilters'
import {
  type DocumentQaFilterState,
  isFilterComplete,
  pathKey,
  searchParamsToState,
  stateToSearchParams,
} from './utils'

export const DocumentQaPage = () => {
  const { t } = useTranslation()
  const { brandAccountId: brandAccountIdParam } = usePathParams(pathKey)
  const brandAccountId = ensureExists(
    brandAccountIdParam,
    t('assertion_failed'),
  )
  const { ask, clearResult, isLoading, rehydrate, result } =
    useAskDocumentQuestion()
  const { isPending: filtersPending, surveys } =
    useDocumentQaFilters(brandAccountId)
  const [searchParams, setSearchParams] = useSearchParams()
  const logEvent = useLogEvent()

  useLogEventOnMount('Access to document qa')

  // Initialize state from URL params (computed once)
  const [initialState] = React.useState(() => searchParamsToState(searchParams))
  const [filters, setFilters] = React.useState<DocumentQaFilterState>(
    initialState.filters,
  )
  const [currentQuestion, setCurrentQuestion] = React.useState(
    initialState.question,
  )
  // Tracks the requestId of the displayed results so click events can
  // reference the originating search (URL rehydrate seeds it; a new search
  // overwrites it via onRequestId).
  const [currentRequestId, setCurrentRequestId] = React.useState(
    initialState.requestId,
  )

  // Rehydrate results from URL requestId on mount
  React.useEffect(() => {
    if (initialState.requestId) {
      rehydrate(initialState.requestId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // A stale/shared URL can restore filter ids that no longer exist in the
  // loaded survey data. Once the data has loaded, clear any restored id that
  // does not resolve so the dropdowns, isFilterComplete, and the enabled state
  // all agree (instead of looking enabled but silently aborting on submit).
  React.useEffect(() => {
    // Only run after the filter data has actually loaded (not while pending) —
    // otherwise we'd wrongly clear valid restored ids before surveys arrive.
    if (filtersPending) {
      return
    }
    const survey = surveys.find((s) => s.id === filters.surveyId)
    const brandGroup = survey?.brandGroups.find(
      (bg) => String(bg.id) === filters.brandGroupId,
    )
    const brand = brandGroup?.brands.find(
      (b) => String(b.id) === filters.brandId,
    )
    const next = {
      brandGroupId:
        filters.brandGroupId !== null && !brandGroup
          ? null
          : filters.brandGroupId,
      brandId: filters.brandId !== null && !brand ? null : filters.brandId,
      surveyId: filters.surveyId !== null && !survey ? null : filters.surveyId,
    }
    // No-op guard: a valid restored search is untouched and still rehydrates
    // normally. This also prevents a re-render loop.
    if (
      next.surveyId === filters.surveyId &&
      next.brandGroupId === filters.brandGroupId &&
      next.brandId === filters.brandId
    ) {
      return
    }

    // Filters were actually cleared. The same stale/shared URL also rehydrated
    // a requestId (the old result), so the page would otherwise keep showing
    // the OLD answer next to now-empty dropdowns. Drop the displayed result,
    // the tracked request id, and the stale requestId in the URL so everything
    // agrees with the cleaned filter state.
    setFilters(next)
    clearResult()
    setCurrentRequestId(null)
    setSearchParams(stateToSearchParams(currentQuestion, next, null), {
      replace: true,
    })
  }, [
    filtersPending,
    surveys,
    filters,
    currentQuestion,
    clearResult,
    setSearchParams,
  ])

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('documentManagement'),
      createBreadcrumb(pathKey, { brandAccountId }),
    ],
    [brandAccountId, createBreadcrumb],
  )

  const filterComplete = isFilterComplete(filters)

  const handleSubmit = React.useCallback(
    (question: string) => {
      setCurrentQuestion(question)

      // Filter state is ID-based; the backend mutation expects names. Resolve
      // the selected ids to their display names via the loaded filter data.
      const survey = surveys.find((s) => s.id === filters.surveyId)
      const brandGroup = survey?.brandGroups.find(
        (bg) => String(bg.id) === filters.brandGroupId,
      )
      const brand = brandGroup?.brands.find(
        (b) => String(b.id) === filters.brandId,
      )

      // A stale/shared URL can carry filter ids no longer present in the
      // loaded survey data, which would silently resolve to undefined names
      // and send an effectively unfiltered request. Abort instead.
      const hasUnresolvedFilter =
        (filters.surveyId !== null && !survey) ||
        (filters.brandGroupId !== null && !brandGroup) ||
        (filters.brandId !== null && !brand)
      if (hasUnresolvedFilter) {
        return
      }

      ask({
        brandAccountId,
        filters: {
          brandGroupName: brandGroup?.name,
          brandName: brand?.name,
          surveyTitle: survey?.title,
        },
        onRequestId: (requestId) => {
          setCurrentRequestId(requestId)
          setSearchParams(stateToSearchParams(question, filters, requestId), {
            replace: true,
          })
          logEvent('Search document qa')
        },
        question,
      })
    },
    [ask, brandAccountId, filters, logEvent, setSearchParams, surveys],
  )

  const handleFiltersChange = React.useCallback(
    (newFilters: DocumentQaFilterState) => {
      setFilters(newFilters)
      clearResult()
      setSearchParams(stateToSearchParams(currentQuestion, newFilters, null), {
        replace: true,
      })
    },
    [clearResult, currentQuestion, setSearchParams],
  )

  return (
    <PageContainer
      breadcrumbs={breadcrumbs}
      executing={isLoading || filtersPending}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <p>{t('document_qa_description')}</p>
        <DocumentQaFilters
          filters={filters}
          options={{ surveys }}
          onFiltersChange={handleFiltersChange}
        />
        <DocumentQaForm
          hintMessage={
            filterComplete ? undefined : t('document_qa_filter_select_required')
          }
          initialQuestion={currentQuestion}
          isDisabled={!filterComplete || filtersPending}
          isLoading={isLoading}
          onSubmit={handleSubmit}
        />
        {/* `result` is only set once the query produced a final outcome (cards / no-result / error); `currentRequestId` only exists on the success path, so gating display on it hid mutation-phase errors. */}
        {result && (
          <DocumentQaResults
            brandAccountId={brandAccountId}
            requestId={currentRequestId ?? undefined}
            results={result}
          />
        )}
      </div>
    </PageContainer>
  )
}
